import type { MaybeRefOrGetter, Ref } from 'vue';
import type { FormSchemaFieldValue } from '#layers/block-engine/types/form-schema';
import { toValue } from 'vue';
import { get, set } from '#layers/base/utils/object';
import { setCopyValue } from './copy-value';

export interface FormSchemaAdapterOptions {
	/** Called after every adapter write. */
	onChange?: () => void;
}

export interface FormSchemaAdapterField<_TValues extends object, TName extends string = string> {
	/** Dot path to the value in the root form values. */
	name: TName;
}

export interface FormSchemaAdapterGetOptions<TValues extends object, TName extends string = string> {
	/** Field whose value should be read. */
	field: FormSchemaAdapterField<TValues, TName>;
}

export interface FormSchemaAdapterSetOptions<TValues extends object, TName extends string = string> extends FormSchemaAdapterGetOptions<TValues, TName> {
	/** New field value. */
	value: FormSchemaFieldValue<TValues, NoInfer<TName>>;
}

/**
 * Defines how FormSchemaFields should read and write form values.
 *
 * The adapter owns the source and notification behavior, so the renderer does
 * not need to distinguish immutable and mutable write strategies.
 */
export interface FormSchemaAdapter<TValues extends object = Record<string, unknown>> {
	/** Read the current root values snapshot used for schema resolution. */
	getValues: () => TValues;
	/** Read a field value from the adapter source. */
	get: <const TName extends string>(options: FormSchemaAdapterGetOptions<TValues, TName>) => FormSchemaFieldValue<TValues, TName>;
	/** Write a field value through the adapter source. */
	set: <const TName extends string>(options: FormSchemaAdapterSetOptions<TValues, TName>) => void;
}

/**
 * Create an adapter that writes by replacing the source ref value.
 *
 * Top-level writes only shallow-copy the root model. Nested writes clone the
 * changed branch so sibling branches keep their object identity.
 */
export function createCopyAdapter<TValues extends object>(
	model: Ref<TValues>,
	options?: FormSchemaAdapterOptions,
): FormSchemaAdapter<TValues> {
	return {
		getValues: () => model.value,
		get<const TName extends string>({ field }: FormSchemaAdapterGetOptions<TValues, TName>): FormSchemaFieldValue<TValues, TName> {
			return get(model.value as Record<string, unknown>, field.name) as FormSchemaFieldValue<TValues, TName>;
		},
		set<const TName extends string>({ field, value }: FormSchemaAdapterSetOptions<TValues, TName>): void {
			model.value = setCopyValue(model.value, field.name, value);
			options?.onChange?.();
		},
	};
}

/**
 * Create an adapter that writes into the current source object.
 *
 * Nuxt UI form state and RStore-backed records rely on object identity, so this
 * strategy mutates the object returned by the ref or getter.
 */
export function createMutationAdapter<TValues extends object>(
	values: MaybeRefOrGetter<TValues>,
	options?: FormSchemaAdapterOptions,
): FormSchemaAdapter<TValues> {
	return {
		getValues: () => toValue(values),
		get<const TName extends string>({ field }: FormSchemaAdapterGetOptions<TValues, TName>): FormSchemaFieldValue<TValues, TName> {
			return get(toValue(values) as Record<string, unknown>, field.name) as FormSchemaFieldValue<TValues, TName>;
		},
		set<const TName extends string>({ field, value }: FormSchemaAdapterSetOptions<TValues, TName>): void {
			set(toValue(values) as Record<string, unknown>, field.name, value);
			options?.onChange?.();
		},
	};
}
